import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Check, ChevronDown, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import BuildLifecycleStageBadge from './BuildLifecycleStageBadge';
import { useUpdateLifecycleStageBuild } from '@/hooks/maintenance/useUpdateLifecycleStageBuild';
import { useCanOverrideLifecycle } from '@/hooks/maintenance/useCanOverrideLifecycle';
import {
  LIFECYCLE_STAGE_BUILD_LABELS,
  type LifecycleStageBuild,
} from '@/constants/lifecycleStages';

interface Props {
  projectId: string;
  stage: LifecycleStageBuild | null;
  editable?: boolean;
}

const OPTIONS: Array<{ value: LifecycleStageBuild | null; label: string }> = [
  { value: null, label: 'Chưa set' },
  { value: 'm1_open', label: LIFECYCLE_STAGE_BUILD_LABELS.m1_open },
  { value: 'm2_structural', label: LIFECYCLE_STAGE_BUILD_LABELS.m2_structural },
  { value: 'm3_mep', label: LIFECYCLE_STAGE_BUILD_LABELS.m3_mep },
  { value: 'm4_finishing', label: LIFECYCLE_STAGE_BUILD_LABELS.m4_finishing },
  { value: 'm5_acceptance', label: LIFECYCLE_STAGE_BUILD_LABELS.m5_acceptance },
];

const isRlsError = (err: any): boolean => {
  const msg = (err?.message || '').toLowerCase();
  return (
    err?.code === '42501' ||
    msg.includes('row-level security') ||
    msg.includes('permission denied') ||
    msg.includes('insufficient_privilege')
  );
};

export default function BuildInlineStageCell({ projectId, stage, editable = true }: Props) {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const canEdit = useCanOverrideLifecycle() && editable;
  const mutation = useUpdateLifecycleStageBuild();

  if (!canEdit) {
    return <BuildLifecycleStageBadge stage={stage} />;
  }

  const handlePick = (next: LifecycleStageBuild | null) => {
    setOpen(false);
    if (next === stage) return;

    // Optimistic patch across all projects-optimized-v2 cached pages/filters
    const snapshots = qc.getQueriesData({ queryKey: ['projects-optimized-v2'] }).map(
      ([key, data]) => {
        qc.setQueryData(key, (old: any) => {
          if (!old?.projects) return old;
          return {
            ...old,
            projects: old.projects.map((p: any) =>
              p.id === projectId ? { ...p, lifecycle_stage_build: next } : p,
            ),
          };
        });
        return [key, data] as const;
      },
    );

    mutation.mutate(
      { projectId, stage: next },
      {
        onError: (err: any) => {
          // Revert cache snapshot on ANY error (including 42501 RLS)
          snapshots.forEach(([k, d]) => qc.setQueryData(k, d));
          if (isRlsError(err)) {
            toast.error('Bạn không có quyền sửa Build Lifecycle Stage');
          }
          // useUpdateLifecycleStageBuild already toasts generic errors otherwise
        },
      },
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className="cursor-pointer inline-flex items-center gap-1"
          disabled={mutation.isPending}
        >
          {mutation.isPending ? (
            <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" /> Đang lưu…
            </span>
          ) : (
            <>
              <BuildLifecycleStageBadge stage={stage} />
              <ChevronDown className="h-3 w-3 text-muted-foreground" />
            </>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-44 p-1"
        align="start"
        onClick={(e) => e.stopPropagation()}
      >
        {OPTIONS.map((opt) => {
          const isCurrent = (opt.value == null && stage == null) || opt.value === stage;
          return (
            <Button
              key={opt.value ?? 'unset'}
              variant="ghost"
              size="sm"
              className={cn('w-full justify-start gap-2 h-8', isCurrent && 'font-semibold')}
              onClick={() => handlePick(opt.value)}
            >
              <Check className={cn('h-3 w-3', isCurrent ? 'opacity-100' : 'opacity-0')} />
              {opt.label}
            </Button>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}
